// components/inlineCommentPanel/InlineCommentPanel.jsx
import React, { useEffect, useRef, useState, useMemo } from "react";
import "./inlineCommentPanel.css";
import { toast } from "react-toastify";

export default function InlineCommentPanel({
  draft,
  onAddInlineComment,
  onDeleteInlineComment,
  currentUser,
  canPost = false,
  hasSelection = false,
  onClearInlineSelection,
  pendingQuote = "",
  activeInlineCommentId = null,
  onSelectInlineComment,
  mentionUsers = [],
  }) {

  const [newInlineComment, setNewInlineComment] = useState("");
  const [submittingInlineComment, setSubmittingInlineComment] = useState(false);
  const commentRefs = useRef({});

  const [mentionQuery, setMentionQuery] = useState("");
  const [showMentionMenu, setShowMentionMenu] = useState(false);
  const inlineInputRef = useRef(null);

  const roleMap = {
    1: "Reader",
    2: "Writer",
    3: "Editor",
    4: "Lead Editor",
    5: "Admin",
  };

  const dirty = newInlineComment.trim().length > 0;

  const filteredMentionUsers = useMemo(() => {
    if (!showMentionMenu) return [];
    if (!mentionQuery) return mentionUsers;
    const q = mentionQuery.toLowerCase();
    return mentionUsers.filter(
      (u) =>
        u.username.toLowerCase().startsWith(q) ||
        (u.displayName || "").toLowerCase().includes(q)
    );
  }, [mentionQuery, mentionUsers, showMentionMenu]);

  const handleSelectInlineMention = (u) => {
    setNewInlineComment((prev) => {
      const textarea = inlineInputRef.current;
      const cursorPos = textarea ? textarea.selectionStart : prev.length;
      const before = prev.slice(0, cursorPos);
      const after = prev.slice(cursorPos);

      const replacedBefore = before.replace(
        /@([A-Za-z0-9_]*)$/,
        `@${u.username}`
      );

      const nextText = replacedBefore + after;

      requestAnimationFrame(() => {
        if (textarea) {
          textarea.focus();
          const newPos = replacedBefore.length;
          textarea.setSelectionRange(newPos, newPos);
        }
      });

      return nextText;
    });

    setMentionQuery("");
    setShowMentionMenu(false);
  };

  const renderCommentText = (text) => {
    const raw = text || "";
    const parts = raw.split(/(@[A-Za-z0-9_]+)/g);

    return parts.map((part, idx) => {
      if (/^@[A-Za-z0-9_]+$/.test(part)) {
        return (
          <span key={idx} className="comment-mention">
            {part}
          </span>
        );
      }
      return <span key={idx}>{part}</span>;
    });
  };

  useEffect(() => {
    if (!activeInlineCommentId) return;
    const el = commentRefs.current[activeInlineCommentId];
    if (el) {
      el.scrollIntoView({ block: "nearest", behavior: "smooth" });
    }
  }, [activeInlineCommentId]);

  const handleAddInlineComment = async () => {
    const text = newInlineComment.trim();
    if (!text || !canPost) return;

    setSubmittingInlineComment(true);
    try {
      await onAddInlineComment?.(draft?._id, text);
      setNewInlineComment("");
      setMentionQuery("");
      setShowMentionMenu(false);
      onClearInlineSelection?.();
    } catch (err) {
      console.error(err);
      toast.error(err?.response?.data?.message || "Failed to save inline comment.");
    } finally {
      setSubmittingInlineComment(false);
    }
  };

  const handleCancelInlineComment = () => {
    setNewInlineComment("");
    setMentionQuery("");
    setShowMentionMenu(false);
    onClearInlineSelection?.();
  };

  return (
    <div className="dc-inline-body">
      <div className="dc-inline-section">

        {/*
        <div className="dc-section-title">
          Inline Comments
        </div>
        */}

        <div className="dc-hint">
          Use inline comments to provide detailed feedback about a specific excerpt from the draft text.
        </div>

        {hasSelection && pendingQuote && (
          <div
            className="dc-quote-preview"
            style={{
              fontStyle:'italic',
              background:'#f8f8f8',
              padding:'6px 8px',
              borderLeft:'3px solid #ddd',
              borderRadius:4,
              margin:'8px 0',
              display: "flex",
              flexDirection: "column",
            }}
          >
            <div style={{ color: "#333", whiteSpace: "pre-wrap", lineHeight: 1.4 }}>
              “{pendingQuote}”
            </div>

            <button
              type="button"
              onClick={onClearInlineSelection}
              style={{
                alignSelf: "flex-end",
                background: "transparent",
                border: "none",
                color: "#888",
                fontSize: "12px",
                cursor: "pointer",
                padding: 0,
                marginTop: "6px",
                transition: "color 0.2s ease",
              }}
              onMouseOver={(e) => (e.currentTarget.style.color = "#555")}
              onMouseOut={(e) => (e.currentTarget.style.color = "#888")}
            >
              ✕ Clear Quote
            </button>
          </div>
        )}

        <textarea
          className="dc-notes"
          placeholder={
            !canPost
              ? "Save draft to enable inline comments"
              : !hasSelection
                ? "Highlight text in the editor and click “Add Inline Comment” first…"
                : "Share a new inline comment…"
          }
          value={newInlineComment}
          ref={inlineInputRef}
          onChange={(e) => {
            const value = e.target.value;
            setNewInlineComment(value);

            const cursorPos = e.target.selectionStart ?? value.length;
            const textBeforeCursor = value.slice(0, cursorPos);
            const match = textBeforeCursor.match(/@([A-Za-z0-9_]*)$/);

            if (match) {
              setMentionQuery(match[1]);
              setShowMentionMenu(true);
            } else {
              setMentionQuery("");
              setShowMentionMenu(false);
            }
          }}
          onKeyDown={(e) => {
            if (e.key === "Escape") {
              setShowMentionMenu(false);
              setMentionQuery("");
            }
          }}
          rows={5}
          disabled={!canPost || !hasSelection}
        />
      </div>

      {showMentionMenu && filteredMentionUsers.length > 0 && (
        <div className="mention-menu">
          {filteredMentionUsers.map((u) => (
            <button
              key={u.id}
              type="button"
              className="mention-menu-item"
              onClick={() => handleSelectInlineMention(u)}
            >
              @{u.username}{" "}
              <span className="mention-display-name">{u.displayName}</span>
            </button>
          ))}
        </div>
      )}

      <footer className="dc-footer">
        <button
          className="dc-btn ghost"
          onClick={handleCancelInlineComment}
          disabled={!dirty}
        >
          Cancel
        </button>

        <button
          className="dc-btn primary"
          onClick={handleAddInlineComment}
          disabled={!canPost || !hasSelection || !dirty || submittingInlineComment}
          aria-disabled={!canPost || !hasSelection || !dirty || submittingInlineComment}
        >
          {submittingInlineComment ? "Posting..." : "Add Comment"}
        </button>
      </footer>

      <div className="dc-comments">
        {[...(draft.inlineComments || [])]
          .sort((a, b) => new Date(b.timestamp || b.createdAt) - new Date(a.timestamp || a.createdAt))
          .map((c) => {
            const when = c.timestamp || c.createdAt;
            const canDelete = currentUser?.role >= 4 || String(c.authorId) === String(currentUser?.id);
            return (
              <div
                key={c._id}
                id={`inline-comment-${c._id}`}
                ref={(el) => { commentRefs.current[c._id] = el; }}
                className={
                  "dc-comment" +
                  (activeInlineCommentId === c._id ? " dc-comment--active" : "")
                }
                onClick={() => onSelectInlineComment?.(c)}
              >
                <div className="dc-comment-header">
                  <span className="dc-comment-author">{c.authorName || "Unknown User"}</span>
                  {c.role && <span className="dc-comment-role">({roleMap[c.role] || "Unknown Role"}){" "}</span>}
                  {when && (
                    <span className="dc-comment-time">
                      {new Date(when).toLocaleString("en-US", {
                        year: "numeric",
                        month: "short",
                        day: "numeric",
                        hour: "2-digit",
                        minute: "2-digit",
                      })}
                    </span>
                  )}
                  {canDelete && (
                    <button
                      className="dc-comment-delete"
                      title="Delete comment"
                      onClick={(e) => {
                        e.stopPropagation();
                        onDeleteInlineComment?.(draft._id, c._id);
                      }}
                    >
                      Delete
                    </button>
                  )}
                </div>
                <div className="dc-comment-body">
                  {c.quoteText && (
                    <div className="dc-quote">“{c.quoteText}”</div>
                  )}
                  <div className="dc-comment-text">
                    {renderCommentText(c.text)}
                  </div>
                </div>
              </div>
            );
          })}
      </div>
    </div>
  );
}
